/***************************************************************************/
"use client";
/***************************************************************************/

import { useFetcher } from "./use-fetcher";

/**
 * A hook to handle fetch requests with support for request queuing and cancellation.
 * Provides methods for GET, POST, PUT, PATCH, and DELETE requests.
 * @returns {object} An object containing fetch methods for various HTTP verbs.
 */
const useFetch = () => {
  const fetcher = useFetcher();

  /**
   * Utility function to determine if request data is FormData.
   * @param data - The request data to check.
   * @returns True if the data is FormData, otherwise false.
   */
  const isFormData = (data: unknown): data is FormData =>
    typeof FormData !== "undefined" && data instanceof FormData;

  return {
    /**
     * Performs a fetch request using the configured fetcher.
     */
    fetch: fetcher,

    /**
     * GET request helper method.
     * @template TResponseData - The expected response data type.
     * @param {string | URL} url - The endpoint to send the request to.
     * @param {RequestInit} [options] - Fetch options excluding method and body.
     * @param {{ timeout?: number; key?: string }} [config] - Optional timeout and key-based config.
     * @returns {Promise<TFetchResponse<TResponseData | undefined>>} The fetch response.
     */
    get: async <TResponseData>(
      url: string | URL,
      options?: Omit<RequestInit, "method" | "body">,
      config?: { timeout?: number; key?: string },
    ) => {
      const derivedOptions: RequestInit = {
        ...options,
        method: "GET",
      };
      return await fetcher<TResponseData>(url, derivedOptions, config);
    },

    /**
     * POST request helper method.
     * @template TRequestData - The request data type.
     * @template TResponseData - The response data type.
     * @param {string | URL} url - The endpoint to send the request to.
     * @param {TRequestData} requestData - The data to be sent in the request body.
     * @param {RequestInit} [options] - Fetch options excluding method and body.
     * @param {{ timeout?: number; key?: string }} [config] - Optional timeout and key-based config.
     * @returns {Promise<TFetchResponse<TResponseData | undefined>>} The fetch response.
     */
    post: async <TRequestData, TResponseData>(
      url: string | URL,
      requestData: TRequestData,
      options?: Omit<RequestInit, "method" | "body">,
      config?: { timeout?: number; key?: string },
    ) => {
      const derivedOptions: RequestInit = {
        ...options,
        method: "POST",
        body: isFormData(requestData)
          ? requestData
          : JSON.stringify(requestData),
        headers: isFormData(requestData)
          ? options?.headers
          : {
              ...(options?.headers || {}),
              "Content-Type": "application/json",
            },
      };
      return await fetcher<TResponseData>(url, derivedOptions, config);
    },

    /**
     * PUT request helper method.
     * @template TRequestData - The request data type.
     * @template TResponseData - The response data type.
     * @param {string | URL} url - The endpoint to send the request to.
     * @param {TRequestData} requestData - The data to be sent in the request body.
     * @param {RequestInit} [options] - Fetch options excluding method and body.
     * @param {{ timeout?: number; key?: string }} [config] - Optional timeout and key-based config.
     * @returns {Promise<TFetchResponse<TResponseData | undefined>>} The fetch response.
     */
    put: async <TRequestData, TResponseData>(
      url: string | URL,
      requestData: TRequestData,
      options?: Omit<RequestInit, "method" | "body">,
      config?: { timeout?: number; key?: string },
    ) => {
      const derivedOptions: RequestInit = {
        ...options,
        method: "PUT",
        body: isFormData(requestData)
          ? requestData
          : JSON.stringify(requestData),
        headers: isFormData(requestData)
          ? options?.headers
          : {
              ...(options?.headers || {}),
              "Content-Type": "application/json",
            },
      };
      return await fetcher<TResponseData>(url, derivedOptions, config);
    },

    /**
     * PATCH request helper method.
     * @template TRequestData - The request data type.
     * @template TResponseData - The response data type.
     * @param {string | URL} url - The endpoint to send the request to.
     * @param {TRequestData} requestData - The data to be sent in the request body.
     * @param {RequestInit} [options] - Fetch options excluding method and body.
     * @param {{ timeout?: number; key?: string }} [config] - Optional timeout and key-based config.
     * @returns {Promise<TFetchResponse<TResponseData | undefined>>} The fetch response.
     */
    patch: async <TRequestData, TResponseData>(
      url: string | URL,
      requestData: TRequestData,
      options?: Omit<RequestInit, "method" | "body">,
      config?: { timeout?: number; key?: string },
    ) => {
      const derivedOptions: RequestInit = {
        ...options,
        method: "PATCH",
        body: isFormData(requestData)
          ? requestData
          : JSON.stringify(requestData),
        headers: isFormData(requestData)
          ? options?.headers
          : {
              ...(options?.headers || {}),
              "Content-Type": "application/json",
            },
      };
      return await fetcher<TResponseData>(url, derivedOptions, config);
    },

    /**
     * DELETE request helper method.
     * @template TRequestData - The request data type.
     * @template TResponseData - The response data type.
     * @param {string | URL} url - The endpoint to send the request to.
     * @param {TRequestData} requestData - The data to be sent in the request body.
     * @param {RequestInit} [options] - Fetch options excluding method and body.
     * @param {{ timeout?: number; key?: string }} [config] - Optional timeout and key-based config.
     * @returns {Promise<TFetchResponse<TResponseData | undefined>>} The fetch response.
     */
    delete: async <TRequestData, TResponseData>(
      url: string | URL,
      requestData: TRequestData,
      options?: Omit<RequestInit, "method" | "body">,
      config?: { timeout?: number; key?: string },
    ) => {
      const derivedOptions: RequestInit = {
        ...options,
        method: "DELETE",
        body: isFormData(requestData)
          ? requestData
          : JSON.stringify(requestData),
        headers: isFormData(requestData)
          ? options?.headers
          : {
              ...(options?.headers || {}),
              "Content-Type": "application/json",
            },
      };
      return await fetcher<TResponseData>(url, derivedOptions, config);
    },
  };
};

export { useFetch };
